import { useTranslation } from 'react-i18next';

import { ArrowDownRight, ArrowUpRight, MoveRight } from 'lucide-react';

import { BREATH_SEGMENTS } from '@muzly/voice-engine';

import { BreathLineChart } from './charts';
import { ItemRow } from './ResultRow';
import { Card, Pill, SectionHeadCard } from '../../../../components';
import { scoreLevel } from '../../resultCopy';

import type { PillTone } from '../../../../components';
import type { DiagnosisRecord } from '../../record';
import type { QualitativeLevel } from '../../resultCopy';

/**
 * 호흡 안정도 — 지속음의 시작·중간·후반 흔들림(시안 §8.7 `1000:8628`): 범례 카드 · 라인
 * 차트 · 세 칸 점수 · `구간별 안정도` 행.
 *
 * 종합 점수 한 줄(`소리 안정성`, 진단 1)과 **겹치지 않는다**: 저건 테이크 전체의 흔들림이고
 * 여기는 그 흔들림이 언제 있었는지다. 한 숫자로는 처음에 흔들리다 잡힌 사람과 잘 시작했다가
 * 숨이 떨어진 사람이 똑같이 보이는데, 두 사람에게 할 말은 정반대다.
 *
 * **구간 경계는 엔진이 소유한다**(`BREATH_SEGMENTS`). 화면이 `0~3초`를 자기 문자열에 박으면
 * 경계가 움직이는 날 라벨만 옛날 값을 말한다.
 *
 * 시안의 `평균 기준선`은 비교할 평균이 없어 그리지 않는다.
 */

const TONE: Record<QualitativeLevel, PillTone> = {
  high: 'pink',
  mid: 'mint',
  low: 'orange',
};

const ICON: Record<QualitativeLevel, typeof ArrowUpRight> = {
  high: ArrowUpRight,
  mid: MoveRight,
  low: ArrowDownRight,
};

export const BreathSection = ({ record }: { record: DiagnosisRecord }) => {
  const { t } = useTranslation();
  const scores = record.breath ?? [];
  const scoreOf = (id: string): number | null =>
    scores.find((b) => b.id === id)?.score ?? null;

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-col gap-8 py-4">
        <Card className="mx-[18px] flex items-center gap-4 px-5 py-[18px] text-sm font-medium text-bk-800">
          <span className="flex items-center gap-2">
            <span
              aria-hidden="true"
              className="h-[3px] w-4 rounded-sm bg-primary"
            />
            {t('web.result.breath.legendScore')}
          </span>
          {/* 평균 선은 차트에 안 넘긴다(`BreathLineChart`의 `average` 미사용) —
              범례만 남기면 없는 점선을 약속한다. */}
        </Card>

        {/* 못 잰 회차에서는 차트가 스스로 안 그려진다. */}
        {scores.length === 0 ? (
          <p className="text-center text-sm font-semibold text-bk-500">
            {t('web.result.pending')}
          </p>
        ) : (
          <BreathLineChart
            segments={BREATH_SEGMENTS}
            scores={scores}
            series={record.pitchSeries}
          />
        )}

        <div className="mx-[18px] grid grid-cols-3 gap-2 text-center">
          {BREATH_SEGMENTS.map(({ id, fromSec, toSec }) => {
            const score = scoreOf(id);
            return (
              <div key={id} className="flex flex-col items-center gap-1">
                <span className="text-sm font-semibold text-bk-900">
                  {t(`web.result.breath.name.${id}`)}
                </span>
                <span className="text-xs text-bk-600">
                  {t('web.result.breath.axisSec', { value: fromSec })} ~{' '}
                  {t('web.result.breath.axisSec', { value: toSec })}
                </span>
                <span
                  className={
                    score == null
                      ? 'mt-1 text-xs font-medium text-bk-500'
                      : scoreLevel(score) === 'low'
                        ? 'mt-1 text-xl font-bold tabular-nums text-orange'
                        : 'mt-1 text-xl font-bold tabular-nums text-primary'
                  }
                >
                  {score == null
                    ? t('web.result.pending')
                    : t('web.result.breath.point', { value: score })}
                </span>
              </div>
            );
          })}
        </div>
      </div>

      <div className="flex flex-col gap-8 py-4">
        <SectionHeadCard
          size="md"
          className="mx-[18px]"
          title={t('web.result.breath.bySegment')}
        />
        <div className="flex flex-col gap-2">
          {BREATH_SEGMENTS.map(({ id, fromSec, toSec }) => {
            // 배열에 없는 구간 = 테이크가 거기까지 안 갔다. 줄은 남기되 값은 "측정 준비 중"이다 —
            // 줄까지 지우면 세 구간 중 무엇이 빠졌는지 화면에서 사라진다.
            const score = scoreOf(id);
            const level = score == null ? null : scoreLevel(score);
            const Icon = level ? ICON[level] : MoveRight;
            return (
              <ItemRow
                key={id}
                icon={
                  <Icon
                    className={
                      level === 'low'
                        ? 'h-5 w-5 text-orange'
                        : level
                          ? 'h-5 w-5 text-primary'
                          : 'h-5 w-5 text-bk-400'
                    }
                    aria-hidden="true"
                  />
                }
                title={t('web.result.breath.row', {
                  name: t(`web.result.breath.name.${id}`),
                  from: fromSec,
                  to: toSec,
                })}
                desc={level ? t(`web.result.breath.desc.${level}`) : undefined}
                right={
                  <span className="flex shrink-0 items-center gap-2">
                    {level && score != null && (
                      <span
                        className={
                          level === 'low'
                            ? 'text-base font-bold tabular-nums text-orange'
                            : 'text-base font-bold tabular-nums text-primary'
                        }
                      >
                        {t('web.result.breath.point', { value: score })}
                      </span>
                    )}
                    <Pill tone={level ? TONE[level] : 'gray'}>
                      {level
                        ? t(`web.result.breath.badge.${level}`)
                        : t('web.result.pending')}
                    </Pill>
                  </span>
                }
              />
            );
          })}
        </div>
      </div>
    </div>
  );
};
